import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { getAskAiContext } from "../lib/askAiContext";
import WidgetRenderer from "./WidgetRenderer";
import {
  Sparkles,
  X,
  Send,
  Loader2,
  Bot,
  User,
  LayoutDashboard,
  Wand2
} from "lucide-react";

const QUICK_PROMPTS = [];

export default function AskAIDrawer() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I am Quantira Ask AI. Ask me to create dashboards, reports, or answer ERP analytics questions using approved business data.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("quantira:open-ask-ai", openHandler);
    return () => window.removeEventListener("quantira:open-ask-ai", openHandler);
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  /**
   * Turn an axios failure into readable text.
   *
   * `detail` is not always a string. FastAPI returns a 422 as a LIST of
   * validation objects, and putting that straight into the bubble rendered as
   * "[object Object]" for every message — the user saw that and nothing else,
   * with no clue the request had even failed. Pull out the messages instead.
   */
  const errorText = (e) => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) =>
          typeof d === "string"
            ? d
            : [Array.isArray(d?.loc) ? d.loc.join(".") : null, d?.msg]
                .filter(Boolean)
                .join(": ")
        )
        .filter(Boolean);
      if (msgs.length) return `Request rejected — ${msgs.join("; ")}`;
    }
    if (detail && typeof detail === "object") {
      const msg = detail.msg || detail.message || detail.error;
      if (typeof msg === "string" && msg.trim()) return msg;
      try {
        return `Request rejected — ${JSON.stringify(detail)}`;
      } catch {
        /* fall through to the generic message */
      }
    }
    const status = e?.response?.status;
    if (status) return `AI request failed (HTTP ${status}).`;
    return e?.message || "AI request failed.";
  };

  const sendPrompt = async (textArg) => {
    const text = (textArg || prompt).trim();
    if (!text || loading) return;

    setPrompt("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);

    try {
      const lower = text.toLowerCase();

      // Dashboard-building intent requires BOTH an explicit build verb and an
      // artifact noun. The previous test fired on the bare word "report" or
      // "chart" anywhere in the text, so factual questions such as
      // "show me a chart of revenue" or "monthly sales report" were diverted
      // into dashboard planning and never answered with a number.
      const BUILD_VERB = /\b(create|build|make|generate|design|set up|add a|new)\b/;
      const ARTIFACT = /\b(dashboard|report|chart|graph|widget|visuali[sz]ation|view)\b/;
      const wantsDashboard = BUILD_VERB.test(lower) && ARTIFACT.test(lower);

      const wantsDatasetGuidance =
        lower.includes("what dataset") ||
        lower.includes("which dataset") ||
        lower.includes("datasets required") ||
        lower.includes("data sets required") ||
        lower.includes("what kind of data") ||
        lower.includes("required to create");

      if (wantsDashboard && !wantsDatasetGuidance) {
        const r = await api.post("/ai/analytics-agent/plan", { prompt: text });
        const plan = r.data.plan;
        const provider = r.data.provider || "fallback";

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text:
              `I prepared a dashboard plan using ${provider === "openai" ? "OpenAI" : "fallback logic"}.\n\n` +
              `Dashboard: ${plan.dashboard_name}\n` +
              `Dataset: ${plan.dataset_name || plan.dataset_id || "Not selected"}\n` +
              `Widgets: ${(plan.widgets || []).length}\n` +
              `Filters: ${(plan.filters || []).length}\n\n` +
              `Click "Create Dashboard" if you want me to save it.`,
            plan,
            provider,
            originalPrompt: text,
          },
        ]);
      } else {
        // Carry the CEO dashboard's active filters so Ask AI answers for the
        // period and scope currently on screen, not for all-time.
        const dashboardContext = getAskAiContext();
        const r = await api.post("/ai/ceo-chat/answer", {
          prompt: text,
          history: messages.map((m) => ({
            role: m.role,
            text: m.text,
          })),
          ...(dashboardContext ? { dashboard_context: dashboardContext } : {}),
        });
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: r.data.answer || "I found a result.",
            provider: r.data.provider,
            mode: r.data.mode,
            widget: r.data.widget,
            data: r.data.data,
            reason: r.data.reason,
            originalPrompt: text,
          },
        ]);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: errorText(e),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createDashboard = async (originalPrompt) => {
    if (!originalPrompt || loading) return;
    setLoading(true);

    try {
      const r = await api.post("/ai/analytics-agent/create-dashboard", { prompt: originalPrompt });
      const dashboard = r.data.dashboard;
      const provider = r.data.provider || "fallback";

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            `Dashboard created successfully using ${provider === "openai" ? "OpenAI" : "fallback logic"}.\n\n` +
            `${dashboard.name}`,
          dashboard,
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: e?.response?.data?.detail || e.message || "Dashboard creation failed.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="ask-ai-overlay" data-testid="ask-ai-drawer">
      <div className="ask-ai-backdrop" onClick={() => setOpen(false)} />
      <aside className="ask-ai-panel">
        <div className="ask-ai-head">
          <div className="ask-ai-brand">
            <div className="ask-ai-logo">
              <Sparkles size={18} strokeWidth={1.9} />
            </div>
            <div>
              <div className="ask-ai-title">Quantira Ask AI</div>
              <div className="ask-ai-subtitle">Dashboards, reports, and ERP analytics assistant</div>
            </div>
          </div>
          <button className="ask-ai-close" onClick={() => setOpen(false)}>
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        <div className="ask-ai-messages">
          {messages.map((m, idx) => (
            <div key={idx} className={`ask-ai-msg ${m.role}`}>
              <div className="ask-ai-avatar">
                {m.role === "assistant" ? <Bot size={15} /> : <User size={15} />}
              </div>
              <div className="ask-ai-bubble">
                {m.role==="assistant" ? (() => {
                  const text=String(m.text||"").trim();
                  const parts=text.split(/;\s*/).map(x=>x.trim()).filter(Boolean);
                  const structured=parts.length>=3 && parts.slice(1).some(x=>/:\s*/.test(x));
                  if(!structured) return <div className="ask-ai-text">{text}</div>;
                  const main=parts.shift(), fields=[], formulas=[];
                  parts.forEach(x=>{
                    const f=x.match(/^Formula:\s*(.+)$/i);
                    const v=x.match(/^([^:]+):\s*(.+)$/);
                    if(f) formulas.push(f[1]);
                    else if(v) fields.push(v);
                  });
                  return <div className="ask-ai-structured">
                    <div className="ask-ai-answer-main">{main}</div>
                    {fields.length>0&&<div className="ask-ai-answer-fields">
                      {fields.map((f,i)=><div className="ask-ai-answer-field" key={i}>
                        <div className="ask-ai-answer-label">{f[1]}</div>
                        <div className="ask-ai-answer-value">{f[2]}</div>
                      </div>)}
                    </div>}
                    {formulas.length>0&&<div className="ask-ai-answer-formula">
                      <div className="ask-ai-answer-label">Formula</div>
                      <div className="ask-ai-answer-value">{formulas.join(" · ")}</div>
                    </div>}
                  </div>;
                })() : <div className="ask-ai-text">{m.text}</div>}

                {m.plan && (
                  <div className="ask-ai-plan-card">
                    <div className="ask-ai-plan-title">{m.plan.dashboard_name}</div>
                    <div className="ask-ai-plan-meta">
                      {(m.plan.widgets || []).length} widgets · {(m.plan.filters || []).length} filters · {m.provider}
                    </div>
                    <button
                      className="ask-ai-mini-btn"
                      onClick={() => createDashboard(m.originalPrompt)}
                      disabled={loading}
                    >
                      <Wand2 size={13} strokeWidth={1.8} />
                      Create Dashboard
                    </button>
                  </div>
                )}

                {m.dashboard && (
                  <button
                    className="ask-ai-mini-btn"
                    onClick={() => {
                      window.location.href = `/dashboards/${m.dashboard.id}`;
                    }}
                  >
                    <LayoutDashboard size={13} strokeWidth={1.8} />
                    Open Dashboard
                  </button>
                )}

                {m.data?.columns &&
                  Array.isArray(m.data?.rows) &&
                  m.data.rows.length > 0 && (
                  <div className="ask-ai-data-preview">
                    <div className="ask-ai-data-title">Data Preview</div>
                    <div className="ask-ai-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            {m.data.columns.slice(0, 6).map((c) => <th key={c}>{c}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {(
  /\b(list|show|display|give|all)\b/i.test(m.originalPrompt || "")
    ? m.data.rows
    : m.data.rows
).map((row, ridx) => (
                            <tr key={ridx}>
                              {m.data.columns.slice(0, 6).map((column, cidx) => {
                                const cell = Array.isArray(row)
                                  ? row[cidx]
                                  : row?.[column];

                                return (
                                  <td key={column}>
                                    {String(cell ?? "—")}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                  {m.widget &&
                  (m.widget.dataset_id || m.widget.datasetId) &&
                  !m.data?.columns &&
                  (
                    <div className="ask-ai-widget">
                      <WidgetRenderer
                        widget={{
                          ...m.widget,
                          data: Array.isArray(m.widget.data)
                            ? m.widget.data
                            : Array.isArray(m.data?.rows)
                              ? m.data.rows
                              : undefined,
                        }}
                        dashboardFilters={[]}
                      />
                    </div>
                  )}

              </div>
            </div>
          ))}

          {loading && (
            <div className="ask-ai-msg assistant">
              <div className="ask-ai-avatar"><Bot size={15} /></div>
              <div className="ask-ai-bubble loading">
                <Loader2 size={15} className="animate-spin" />
                Thinking...
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        <form
          className="ask-ai-input-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            sendPrompt();
          }}
        >
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!loading && prompt.trim()) {
                  sendPrompt();
                }
              }
            }}
            placeholder="Ask AI to create a dashboard or report..."
            rows={3}
          />
          <button type="submit" disabled={loading || !prompt.trim()}>
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
          </button>
        </form>

        <div className="ask-ai-foot">
          Ask AI can make mistakes. Verify CEO-level numbers before decisions.
        </div>
      </aside>
    </div>
  );
}
